import Konva from 'konva';
import React from 'react';
import { Circle, Group, Label, Line, Rect, Tag, Text } from 'react-konva';

interface SnapLinesProps {
  snapLines: {
    x?: number;
    y?: number;
    targetX?: number;
    targetY?: number;
    centeredIn?: { left: number; right: number; top: number; bottom: number };
    activeBounds?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    distances?: {
      from: { x: number; y: number };
      to: { x: number; y: number };
      value: number;
      axis: 'horizontal' | 'vertical';
    }[];
  };
}

const DISTANCE_COLOR = '#FF4D94';
const DISTANCE_TICK = 6;
const DISTANCE_LABEL_OFFSET = 10;

const SNAP_LINE_COLOR = '#FF4D94';
const SNAP_LINE_WIDTH = 1;
const SNAP_LINE_DASH = [6, 4];
const SNAP_DOT_RADIUS = 4;

const CENTER_COLOR = '#10b981';
const CENTER_LINE_WIDTH = 2;
const CENTER_LINE_DASH = [10, 6];
const CENTER_PLUS_SIZE = 10;

export const SnapLines: React.FC<SnapLinesProps> = ({ snapLines }) => {
  const isCentered = !!snapLines.centeredIn;

  // Lift the snap overlay above all other shapes in its layer. The
  // SnapLines component is rendered as a sibling of the shape that owns
  // the snap state, so without this it gets covered by other shapes that
  // happen to paint later. moveToTop() runs after each render so the
  // overlay stays on top through React re-renders during the drag.
  const groupRef = React.useRef<Konva.Group>(null);
  React.useEffect(() => {
    if (groupRef.current) {
      groupRef.current.moveToTop();
    }
  });

  // When the active shape is centered inside another, swap the pink edge
  // visuals for a louder green indicator: an outline glow on the container
  // + green crosshair + a "+" marker at the shared center. The pink edge
  // snap lines still apply for non-centered alignments.
  if (isCentered && snapLines.centeredIn && snapLines.x !== undefined && snapLines.y !== undefined) {
    const { left, right, top, bottom } = snapLines.centeredIn;
    const cx = snapLines.x;
    const cy = snapLines.y;
    const active = snapLines.activeBounds;
    return (
      <Group ref={groupRef} listening={false}>
        {/* Soft glow on the container */}
        <Rect
          x={left}
          y={top}
          width={right - left}
          height={bottom - top}
          stroke={CENTER_COLOR}
          strokeWidth={2}
          opacity={0.35}
          shadowColor={CENTER_COLOR}
          shadowBlur={12}
          shadowOpacity={0.7}
          listening={false}
        />
        {/* Crisp dashed outline so the user sees the container boundary clearly */}
        <Rect
          x={left}
          y={top}
          width={right - left}
          height={bottom - top}
          stroke={CENTER_COLOR}
          strokeWidth={1.5}
          dash={CENTER_LINE_DASH}
          listening={false}
        />
        {/* Active shape outline so the user can directly see the active
            shape sits centered inside the container — without this they
            only see the container's center, with no anchor to compare to. */}
        {active && (
          <Rect
            x={active.left}
            y={active.top}
            width={active.right - active.left}
            height={active.bottom - active.top}
            stroke={CENTER_COLOR}
            strokeWidth={1.5}
            opacity={0.9}
            listening={false}
          />
        )}
        {/* Center lines bounded to whichever extent is larger so the
            crosshair always reaches the container even when the active
            shape is small. */}
        <Line
          x={cx}
          points={[
            0,
            Math.min(top, active?.top ?? top),
            0,
            Math.max(bottom, active?.bottom ?? bottom)
          ]}
          stroke={CENTER_COLOR}
          strokeWidth={CENTER_LINE_WIDTH}
          dash={CENTER_LINE_DASH}
          listening={false}
        />
        <Line
          y={cy}
          points={[
            Math.min(left, active?.left ?? left),
            0,
            Math.max(right, active?.right ?? right),
            0
          ]}
          stroke={CENTER_COLOR}
          strokeWidth={CENTER_LINE_WIDTH}
          dash={CENTER_LINE_DASH}
          listening={false}
        />
        {/* "+" marker at the centered point so it reads as a deliberate hit */}
        <Line
          points={[
            cx - CENTER_PLUS_SIZE,
            cy,
            cx + CENTER_PLUS_SIZE,
            cy
          ]}
          stroke={CENTER_COLOR}
          strokeWidth={CENTER_LINE_WIDTH}
          listening={false}
        />
        <Line
          points={[
            cx,
            cy - CENTER_PLUS_SIZE,
            cx,
            cy + CENTER_PLUS_SIZE
          ]}
          stroke={CENTER_COLOR}
          strokeWidth={CENTER_LINE_WIDTH}
          listening={false}
        />
        <Circle
          x={cx}
          y={cy}
          radius={3}
          fill={CENTER_COLOR}
          listening={false}
        />
      </Group>
    );
  }

  return (
    <Group ref={groupRef} listening={false}>
      {snapLines.x !== undefined && (
        <Line
          x={snapLines.x}
          points={[0, -5000, 0, 5000]}
          stroke={SNAP_LINE_COLOR}
          strokeWidth={SNAP_LINE_WIDTH}
          dash={SNAP_LINE_DASH}
          listening={false}
        />
      )}
      {snapLines.y !== undefined && (
        <Line
          y={snapLines.y}
          points={[-5000, 0, 5000, 0]}
          stroke={SNAP_LINE_COLOR}
          strokeWidth={SNAP_LINE_WIDTH}
          dash={SNAP_LINE_DASH}
          listening={false}
        />
      )}
      {/* Dot on the target shape (vertical snap line → dot at target's center Y) */}
      {snapLines.x !== undefined && snapLines.targetY !== undefined && (
        <Circle
          x={snapLines.x}
          y={snapLines.targetY}
          radius={SNAP_DOT_RADIUS}
          fill={SNAP_LINE_COLOR}
          listening={false}
        />
      )}
      {/* Dot on the target shape (horizontal snap line → dot at target's center X) */}
      {snapLines.y !== undefined && snapLines.targetX !== undefined && (
        <Circle
          x={snapLines.targetX}
          y={snapLines.y}
          radius={SNAP_DOT_RADIUS}
          fill={SNAP_LINE_COLOR}
          listening={false}
        />
      )}
      {/* Dot at intersection when both axes snap */}
      {snapLines.x !== undefined && snapLines.y !== undefined && (
        <Circle
          x={snapLines.x}
          y={snapLines.y}
          radius={SNAP_DOT_RADIUS}
          fill={SNAP_LINE_COLOR}
          listening={false}
        />
      )}
      {/* Distance overlay (Alt held during drag): dimension lines with
          end ticks + a pill label showing the world-unit gap. */}
      {snapLines.distances?.map((d, i) => {
        const isHorizontal = d.axis === 'horizontal';
        const labelX = isHorizontal
          ? (d.from.x + d.to.x) / 2
          : d.from.x + DISTANCE_LABEL_OFFSET;
        const labelY = isHorizontal
          ? d.from.y - DISTANCE_LABEL_OFFSET
          : (d.from.y + d.to.y) / 2;
        return (
          <Group key={`dist-${i}`} listening={false}>
            <Line
              points={[d.from.x, d.from.y, d.to.x, d.to.y]}
              stroke={DISTANCE_COLOR}
              strokeWidth={1}
              listening={false}
            />
            {/* End ticks so the dimension line reads as a measurement */}
            {isHorizontal ? (
              <>
                <Line
                  points={[
                    d.from.x,
                    d.from.y - DISTANCE_TICK,
                    d.from.x,
                    d.from.y + DISTANCE_TICK
                  ]}
                  stroke={DISTANCE_COLOR}
                  strokeWidth={1}
                  listening={false}
                />
                <Line
                  points={[
                    d.to.x,
                    d.to.y - DISTANCE_TICK,
                    d.to.x,
                    d.to.y + DISTANCE_TICK
                  ]}
                  stroke={DISTANCE_COLOR}
                  strokeWidth={1}
                  listening={false}
                />
              </>
            ) : (
              <>
                <Line
                  points={[
                    d.from.x - DISTANCE_TICK,
                    d.from.y,
                    d.from.x + DISTANCE_TICK,
                    d.from.y
                  ]}
                  stroke={DISTANCE_COLOR}
                  strokeWidth={1}
                  listening={false}
                />
                <Line
                  points={[
                    d.to.x - DISTANCE_TICK,
                    d.to.y,
                    d.to.x + DISTANCE_TICK,
                    d.to.y
                  ]}
                  stroke={DISTANCE_COLOR}
                  strokeWidth={1}
                  listening={false}
                />
              </>
            )}
            <Label x={labelX} y={labelY} listening={false}>
              <Tag
                fill={DISTANCE_COLOR}
                cornerRadius={3}
                pointerDirection={
                  isHorizontal ? 'down' : 'left'
                }
                pointerWidth={6}
                pointerHeight={4}
              />
              <Text
                text={`${Math.round(d.value)}px`}
                fontSize={11}
                fontStyle="bold"
                fill="white"
                padding={3}
              />
            </Label>
          </Group>
        );
      })}
    </Group>
  );
};
